'use client';

import type {
  AnchorHTMLAttributes,
  CSSProperties,
  FC,
  PropsWithChildren,
  ReactNode,
} from 'react';
import { createElement, useMemo } from 'react';
import { cx } from '../../utils/cx';
import { typography } from '../../themes/DefaultTheme';
import type { RevealProps } from '../Reveal';
import { Reveal } from '../Reveal';
import type { SpacingTypographyProps } from './utils/spacingTypographyHelper';
import type { TypographyProps } from './utils/typographyHelper';
import { typographyStyle } from './utils/typographyHelper';
import { useLocationHash } from './utils/useLocationHash';
import typoStyles from './typography.module.css';
import styles from './Headline.module.css';

interface CustomAnchorProps {
  AnchorComponent?: ReactNode;
  anchorRevealProps?: RevealProps;
  focused?: boolean;
}

export interface HeadlineProps
  extends CustomAnchorProps, TypographyProps, SpacingTypographyProps {
  id?: string;
  className?: string;
  anchor?: boolean | string;
  level: 1 | 2 | 3 | 4 | 5 | 6;
  children?: ReactNode;
}

type AnchorProps = AnchorHTMLAttributes<HTMLAnchorElement> & CustomAnchorProps;

const Anchor: FC<PropsWithChildren<AnchorProps>> = ({
  AnchorComponent,
  anchorRevealProps,
  focused,
  children,
  ...props
}) => (
  <Reveal className={styles.anchorReveal} {...anchorRevealProps}>
    <a className={styles.anchor} data-focused={focused} {...props}>
      {AnchorComponent || children}
    </a>
  </Reveal>
);

export const Headline: FC<PropsWithChildren<HeadlineProps>> = (props) => {
  const {
    id,
    className,
    level,
    anchor = '',
    children = '',
    AnchorComponent,
    anchorRevealProps = {},
    ...rest
  } = props;

  const computedId = useMemo(() => {
    if (typeof children === 'string') {
      return children.toLowerCase().replace(/\s/g, '-');
    }
    return '';
  }, [children]);
  const isFocused = useLocationHash(computedId);

  const token: CSSProperties = typography[`h${level}` as 'h1'];
  const style: CSSProperties = {
    ...typographyStyle(token, rest),
    color: 'var(--color-foreground)',
  };

  return createElement(
    `h${level}`,
    {
      id: id || computedId,
      className: cx(typoStyles.text, styles.headline, className),
      style,
    },
    anchor && (id || computedId) && (
      <Anchor
        key="anchor"
        AnchorComponent={AnchorComponent}
        anchorRevealProps={{
          inactiveOpacity: anchorRevealProps.inactiveOpacity || 0,
          size: anchorRevealProps.size || 240,
          spread: anchorRevealProps.spread || 0,
          ...anchorRevealProps,
        }}
        href={`#${id}`}
        focused={isFocused}
      >
        #
      </Anchor>
    ),
    children
  );
};
